import { ApiError } from '../identity/types';
import { friendlyErrorMessage } from '../../shared/friendlyError';
import { BdErrorCode } from './types';

/**
 * Plain-language copy for the benefits and deductions API's refusals. The shared `friendlyErrorMessage` replaces every 409 with one generic line, which
 * would hide exactly the guidance these screens need (a duplicate name, a plan that needs tax review, a stale version), so the stable `code` the API sends
 * (docs/benefits-deductions-contract.md section 4) picks our own words, and a server-authored message is shown for a code we do not know. Everything else
 * (401, 403, 429, 5xx with its operation id) goes through the shared helper unchanged. Nothing is logged, and a message never echoes what was typed.
 */

const RAW_FALLBACK = /^Request to .+ failed with \d+$/;

export type ErrorContext = 'plan' | 'election' | 'deduction' | 'assignment';

export function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

export function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

export function errorCode(err: unknown): string | null {
  return err instanceof ApiError ? err.code : null;
}

export function isVersionConflict(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === BdErrorCode.VersionConflict;
}

/** True for a refusal that did not change anything and is safe to retry with the SAME idempotency key (a network or server failure). */
export function isRetryable(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true; // a failed fetch never reached the server's rules
  return err.status >= 500 || err.status === 429 || err.status === 408;
}

export const VERSION_CONFLICT_MESSAGE =
  'This was changed by someone else after you opened it, so your changes were not saved. Reload the latest version, then make your changes again.';

export function bdErrorMessage(err: unknown, fallback: string, context: ErrorContext = 'plan'): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case BdErrorCode.VersionConflict:
        return VERSION_CONFLICT_MESSAGE;
      case BdErrorCode.DuplicateName:
        return context === 'deduction' ? 'A deduction with this name already exists. Choose a different name.' : 'A benefit plan with this name already exists. Choose a different name.';
      case BdErrorCode.DuplicateCode:
        return context === 'deduction' ? 'A deduction with this short code already exists. Choose a different short code.' : 'A benefit plan with this short code already exists. Choose a different short code.';
      case BdErrorCode.ConfigurationIncomplete:
        if (context === 'election') return 'This plan needs tax review before an employee can be enrolled. You can still record a waiver.';
        if (context === 'assignment') return 'This deduction cannot be assigned yet. Tax treatment must be approved before it can be used in payroll.';
        return 'Tax treatment must be approved before this can be used in payroll. It stays inactive until then.';
      case BdErrorCode.PlanNotEffective:
        return 'The start date is outside the plan\'s effective dates. Choose a date inside them.';
      case BdErrorCode.PlanInactive:
        return 'This plan is inactive, so it cannot take new elections. Reactivate the plan first.';
      case BdErrorCode.CalculationNotSupportedYet:
        return 'Percentage and other calculation types cannot be activated or assigned yet. Payroll honors only fixed amounts.';
      case BdErrorCode.ProtectedCategory:
        return 'Taxes and statutory deductions are system definitions. They cannot be created, changed or hidden.';
      case BdErrorCode.TaxProfileNotApproved:
        return 'That tax profile is not approved for payroll. Tax treatment must be approved before this can be used in payroll.';
      case BdErrorCode.UnsupportedContributionMethod:
        return 'That contribution method needs review before it can be offered.';
      case BdErrorCode.IdempotencyKeyReused:
        return 'This request was already used for a different assignment. Review the preview again, then confirm.';
      case BdErrorCode.TooManyEmployees:
        return 'Too many employees were selected. Choose between 1 and 500, or use all eligible employees (up to 2,000).';
      case BdErrorCode.CoverageLevelNotOffered:
        return 'Choose a coverage level this plan offers.';
      case BdErrorCode.ExpectedVersionRequired:
        return 'This could not be saved because the version you opened was not sent. Reload it and try again.';
      case BdErrorCode.InvalidQuery:
        return 'That search or filter was not understood. Clear the filters and try again.';
      case BdErrorCode.ConfirmRequired:
        return 'Confirm the assignment to continue.';
      case BdErrorCode.AssignmentNotAllowed:
        return 'This deduction cannot be assigned in bulk. Garnishments and levies are managed from the record of each employee.';
      case BdErrorCode.AllEligibleUnavailable:
        return 'The list of eligible employees could not be read right now. Try again, or choose employees yourself.';
      case BdErrorCode.PlanHasElections:
        return 'A plan that has elections cannot change its contribution method or category. Create a new plan instead.';
      case BdErrorCode.DuplicateActiveAssignment:
        return 'This item is already assigned to this employee. Edit it to change it from a new date, or end it first.';
      case BdErrorCode.DefinitionNotEffective:
        return 'The start date is outside this item\'s effective dates. Choose a date inside them.';
      case BdErrorCode.LimitExceedsDefinition:
        return 'The employee\'s annual limit cannot be more than the limit set on the company item.';
      default:
        break;
    }
    if ((err.status === 400 || err.status === 409) && err.message && !RAW_FALLBACK.test(err.message) && err.messageIsServerAuthored) return err.message;
    if (err.status === 403) return 'You do not have permission to do that. Ask a company administrator if you need access.';
  }
  return friendlyErrorMessage(err, fallback);
}
